/**
 * Контент комментария Activity.
 * Парсит mention-маркеры и scope-маркер сделки, рендерит кликабельные
 * фрагменты текста и делегирует переходы в `useActivityScopeActions`.
 */
import { type ReactNode } from 'react';
import { Text } from '@mantine/core';
import { getActivityInlineMarker } from '@ayocore/activity/client/extensions';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import { useActivityScopeActions } from '../../hooks/useActivityScopeActions';
import classes from './ActivityChatMessageItem.module.css';

type MarkerKind = 'user' | 'deal' | 'contact';

type Marker = {
  kind: MarkerKind;
  start: number;
  end: number;
  userId?: number;
  userLabel?: string;
  contactUuid?: string;
  contactLabel?: string;
};

const MARKER_STYLES: Record<MarkerKind, { color: string; className: string }> = {
  user: {
    color: 'blue.7',
    className: classes.mentionUser,
  },
  deal: {
    color: 'orange.8',
    className: classes.mentionDeal,
  },
  contact: {
    color: 'violet.7',
    className: classes.mentionContact,
  },
};

function isValidMarkerRange(start: number, end: number, contentLength: number): boolean {
  return (
    Number.isFinite(start) &&
    Number.isFinite(end) &&
    start >= 0 &&
    end > start &&
    end <= contentLength
  );
}

/**
 * Inline-маркер scope-сущности в тексте сообщения (например, `#66220` у сделки).
 * Формат задаёт дескриптор сущности через реестр расширений Activity.
 */
function getScopeInlineMarker(item: ActivityChatItem): string {
  return getActivityInlineMarker(item.scope);
}

function buildUserMarkers(item: ActivityChatItem, contentLength: number): Marker[] {
  if (!Array.isArray(item.mentions)) {
    return [];
  }

  const markers: Marker[] = [];

  item.mentions.forEach(mention => {
    const start = Number(mention.start);
    const end = Number(mention.end);
    const userId = Number(mention.user_id);
    const label = typeof mention.label === 'string' ? mention.label.trim() : '';

    if (!isValidMarkerRange(start, end, contentLength)) {
      return;
    }

    markers.push({
      kind: 'user',
      start,
      end,
      ...(Number.isFinite(userId) && userId > 0 ? { userId: Math.trunc(userId) } : {}),
      ...(label ? { userLabel: label } : {}),
    });
  });

  return markers;
}

function buildContactMarkers(item: ActivityChatItem, contentLength: number): Marker[] {
  if (!Array.isArray(item.contact_mentions)) {
    return [];
  }

  const markers: Marker[] = [];

  item.contact_mentions.forEach(mention => {
    const start = Number(mention.start);
    const end = Number(mention.end);
    const uuid = typeof mention.uuid === 'string' ? mention.uuid.trim() : '';
    const label = typeof mention.label === 'string' ? mention.label.trim() : '';

    if (!isValidMarkerRange(start, end, contentLength)) {
      return;
    }
    if (!uuid || !label) {
      return;
    }

    markers.push({
      kind: 'contact',
      start,
      end,
      contactUuid: uuid,
      contactLabel: label,
    });
  });

  return markers;
}

function buildDealMarkers(content: string, dealMarker: string): Marker[] {
  if (!dealMarker) {
    return [];
  }

  const markers: Marker[] = [];
  let searchFrom = 0;

  while (searchFrom < content.length) {
    const start = content.indexOf(dealMarker, searchFrom);
    if (start < 0) {
      break;
    }

    markers.push({
      kind: 'deal',
      start,
      end: start + dealMarker.length,
    });

    searchFrom = start + dealMarker.length;
  }

  return markers;
}

function sortMarkers(markers: Marker[]): Marker[] {
  return markers.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    return a.end - b.end;
  });
}

/**
 * Рендерит текст сообщения с подсветкой упоминаний пользователей и `#`-ссылок на сделки.
 */
export function ActivityChatMessageContent({
  item,
  onInsertUserMention,
}: {
  item: ActivityChatItem;
  onInsertUserMention?: (userId: number, label: string) => void;
}) {
  const { onScopeClick, isScopeClickable } = useActivityScopeActions();
  const content = item.content || '';
  const dealMarker = getScopeInlineMarker(item);
  const dealScope = dealMarker ? (item.scope ?? null) : null;
  const dealMarkerClickable = isScopeClickable(dealScope);
  const markers = sortMarkers([
    ...buildUserMarkers(item, content.length),
    ...buildContactMarkers(item, content.length),
    ...buildDealMarkers(content, dealMarker),
  ]);

  if (markers.length === 0) {
    return content;
  }

  const fragments: ReactNode[] = [];
  let cursor = 0;

  markers.forEach((marker, index) => {
    if (!isValidMarkerRange(marker.start, marker.end, content.length) || marker.start < cursor) {
      return;
    }

    if (marker.start > cursor) {
      fragments.push(
        <span key={`text-${index}-${cursor}`}>{content.slice(cursor, marker.start)}</span>
      );
    }

    const markerStyle = MARKER_STYLES[marker.kind];
    const isDealMarker = marker.kind === 'deal';
    const isUserMarker = marker.kind === 'user';
    const userMentionClickable =
      isUserMarker &&
      typeof marker.userId === 'number' &&
      marker.userId > 0 &&
      typeof marker.userLabel === 'string' &&
      marker.userLabel.trim().length > 0 &&
      Boolean(onInsertUserMention);
    const clickable = (isDealMarker && dealMarkerClickable) || userMentionClickable;
    fragments.push(
      <Text
        key={`mention-${marker.kind}-${index}-${marker.start}`}
        component='span'
        fw={400}
        c={markerStyle.color}
        className={`${classes.mentionBase} ${markerStyle.className} ${clickable ? classes.mentionClickable : ''}`}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={
          clickable
            ? () => {
                if (isDealMarker) {
                  void onScopeClick(dealScope);
                  return;
                }
                if (isUserMarker && marker.userId && marker.userLabel) {
                  onInsertUserMention?.(marker.userId, marker.userLabel);
                }
              }
            : undefined
        }
        onKeyDown={
          clickable
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  if (isDealMarker) {
                    void onScopeClick(dealScope);
                    return;
                  }
                  if (isUserMarker && marker.userId && marker.userLabel) {
                    onInsertUserMention?.(marker.userId, marker.userLabel);
                  }
                }
              }
            : undefined
        }
      >
        {content.slice(marker.start, marker.end)}
      </Text>
    );

    cursor = marker.end;
  });

  if (cursor < content.length) {
    fragments.push(<span key={`tail-${cursor}`}>{content.slice(cursor)}</span>);
  }

  return fragments.length > 0 ? fragments : content;
}
